import { useMemo } from 'react';
import type { ViewAdminUser } from '../api/viewAdminApiService';

// ============================================================================
// Constants
// ============================================================================

export const ROLE_LABELS: Record<ViewAdminUser['role'], string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Administrator',
  CLIENT: 'Klient',
  FACILITY_USER: 'Użytkownik obiektu',
  TECHNICIAN: 'Technik',
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Formats an ISO date string to Polish locale format
 * Format: YYYY/MM/DD, HH:MM:SS
 */
export const formatCreatedAt = (
  isoString: string | null | undefined
): string => {
  if (!isoString) {
    return '—';
  }
  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  const hour = parts.find((p) => p.type === 'hour')?.value;
  const minute = parts.find((p) => p.type === 'minute')?.value;
  const second = parts.find((p) => p.type === 'second')?.value;
  return `${year}/${month}/${day}, ${hour}:${minute}:${second}`;
};

/**
 * Returns the value if it exists and is not empty, otherwise returns '—'
 */
export const orDash = (value: string | null | undefined): string => {
  return value?.trim() ? value : '—';
};

/**
 * Formats phone number by combining country code and phone number
 */
export const formatPhoneNumber = (
  countryCode: string | null | undefined,
  phoneNumber: string | null | undefined
): string => {
  const parts = [countryCode, phoneNumber].filter(Boolean);
  return parts.join(' ').trim() || '—';
};

/**
 * Gets the role label for a given role
 */
export const getRoleLabel = (
  role: ViewAdminUser['role'] | null | undefined
): string => {
  return role ? ROLE_LABELS[role] : '—';
};

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseViewAdminContentReturn {
  // Formatted values
  formattedUserId: string;
  formattedEmail: string;
  formattedPhoneNumber: string;
  formattedRole: string;
  formattedCreatedAt: string;
  formattedAddressLine1: string;
  formattedAddressLine2: string;
  formattedCity: string;
  formattedState: string;
  formattedZipCode: string;
  formattedCountry: string;

  // Utility functions (if needed for additional formatting)
  orDash: (value: string | null | undefined) => string;
}

// ============================================================================
// useViewAdminContent Hook
// ============================================================================

/**
 * Custom hook for managing ViewAdminContent component logic
 * Provides formatted values for user data display
 */
export const useViewAdminContent = (
  user: ViewAdminUser | null
): UseViewAdminContentReturn => {
  // Memoize formatted values to avoid recalculating on every render
  const formattedUserId = useMemo(() => orDash(user?.userId), [user?.userId]);

  const formattedEmail = useMemo(() => orDash(user?.email), [user?.email]);

  const formattedPhoneNumber = useMemo(
    () => formatPhoneNumber(user?.countryCode, user?.phoneNumber),
    [user?.countryCode, user?.phoneNumber]
  );

  const formattedRole = useMemo(() => getRoleLabel(user?.role), [user?.role]);

  const formattedCreatedAt = useMemo(
    () => formatCreatedAt(user?.createdAt),
    [user?.createdAt]
  );

  const formattedAddressLine1 = useMemo(
    () => orDash(user?.addressLine1),
    [user?.addressLine1]
  );

  const formattedAddressLine2 = useMemo(
    () => orDash(user?.addressLine2),
    [user?.addressLine2]
  );

  const formattedCity = useMemo(() => orDash(user?.city), [user?.city]);

  const formattedState = useMemo(() => orDash(user?.state), [user?.state]);

  const formattedZipCode = useMemo(
    () => orDash(user?.zipCode),
    [user?.zipCode]
  );

  const formattedCountry = useMemo(
    () => orDash(user?.country),
    [user?.country]
  );

  return {
    // Formatted values
    formattedUserId,
    formattedEmail,
    formattedPhoneNumber,
    formattedRole,
    formattedCreatedAt,
    formattedAddressLine1,
    formattedAddressLine2,
    formattedCity,
    formattedState,
    formattedZipCode,
    formattedCountry,

    // Utility functions
    orDash,
  };
};
